import { Workflow, Brain, Cpu } from "lucide-react";

const pillars = [
  {
    n: "01",
    icon: Workflow,
    name: "Lean Management",
    color: "gold",
    desc: "Cartographier les flux, identifier les 8 gaspillages, reconcevoir la chaîne de valeur. Pas pour produire plus — pour produire juste.",
    tags: ["Flux de valeur", "Gemba", "Kaizen", "Standards"],
  },
  {
    n: "02",
    icon: Brain,
    name: "Neurosciences",
    color: "sage",
    desc: "Comprendre pourquoi tes équipes résistent, comment elles décident, ce qui ancre durablement un changement. Au-delà du change management standard.",
    tags: ["Biais cognitifs", "Conduite du changement", "Charge mentale"],
  },
  {
    n: "03",
    icon: Cpu,
    name: "IA opérationnelle",
    color: "cobalt",
    desc: "Pas l'IA des slides. L'IA qui automatise les tâches répétitives, fluidifie les décisions, et s'intègre à ton ERP. Avec un ROI mesurable.",
    tags: ["Automatisation", "ERP & RPA", "Cas d'usage chiffrés"],
  },
];

const colorMap = {
  gold: { dot: "dot-lean", text: "text-gold" },
  sage: { dot: "dot-neuro", text: "text-sage" },
  cobalt: { dot: "dot-ia", text: "text-cobalt" },
};

export default function PillarsSection() {
  return (
    <section id="methode" className="bg-pearl-dark py-24 sm:py-32" data-testid="pillars-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-3">
            <div className="font-mono-overline text-mute mb-3">Les 3 piliers</div>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] tracking-tight" data-testid="pillars-title">
              Trois disciplines. <em className="text-mute not-italic font-light">Une seule méthode.</em>
            </h2>
            <p className="mt-6 text-mute text-lg leading-relaxed max-w-3xl">
              Chacune prise isolément donne des résultats partiels. Combinées,
              elles forment une grammaire complète de la transformation
              organisationnelle.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            const c = colorMap[p.color];
            return (
              <article
                key={p.n}
                className="md:col-span-4 bg-pearl p-8 sm:p-10 card-lift border-t-2 border-ink/5 hover:border-t-2 group"
                style={{ borderTopColor: `var(--shin-${p.color === "gold" ? "gold" : p.color === "sage" ? "sage" : "cobalt"})` }}
                data-testid={`pillar-card-${p.color}`}
              >
                <div className="flex items-start justify-between mb-12">
                  <Icon size={28} className={c.text} strokeWidth={1.4} />
                  <span className="font-serif text-6xl text-ink/10 leading-none">{p.n}</span>
                </div>
                <h3 className="font-serif text-2xl mb-4">{p.name}</h3>
                <p className="text-mute text-sm leading-relaxed mb-6">{p.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {p.tags.map((t) => (
                    <span key={t} className="text-[11px] px-3 py-1 border border-ink/15 text-ink/70 font-medium tracking-wide">
                      {t}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
